import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { FileText, Plus, Building, Download } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminInvoices() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    invoice_number: '',
    amount: '',
    status: 'pending',
    due_date: new Date().toISOString().split('T')[0],
    description: '',
    xero_link: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, companiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/invoices`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/companies`, { credentials: 'include' })
      ]);

      if (invoicesRes.ok) {
        const invoicesData = await invoicesRes.json();
        setInvoices(invoicesData);
      }

      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        setCompanies(companiesData);
      }
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          due_date: new Date(formData.due_date).toISOString()
        })
      });

      if (response.ok) {
        toast.success('Invoice created successfully');
        setDialogOpen(false);
        setFormData({
          company_id: '',
          invoice_number: '',
          amount: '',
          status: 'pending',
          due_date: new Date().toISOString().split('T')[0],
          description: '',
          xero_link: ''
        });
        fetchData();
      } else {
        throw new Error('Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'pending':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'overdue':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Invoices">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Invoices">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-secondary" />
            <h2 className="text-2xl font-mono font-bold">Invoice Management</h2>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono">
                <Plus className="h-4 w-4 mr-2" />
                Upload Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-mono">Upload Invoice from Xero</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="company" className="font-mono">Company *</Label>
                  <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {companies.map((company) => (
                        <SelectItem key={company.company_id} value={company.company_id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="xero_link" className="font-mono">Xero Invoice Link *</Label>
                  <Input
                    id="xero_link"
                    type="url"
                    value={formData.xero_link}
                    onChange={(e) => setFormData({ ...formData, xero_link: e.target.value })}
                    required
                    className="font-mono mt-1"
                    placeholder="https://go.xero.com/..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">Paste the shareable link from Xero</p>
                </div>

                <div>
                  <Label htmlFor="invoice_number" className="font-mono">Invoice Number *</Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    required
                    className="font-mono mt-1"
                    placeholder="e.g., INV-2024-001"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount" className="font-mono">Amount (£) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      className="font-mono mt-1"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <Label htmlFor="status" className="font-mono">Status *</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="font-mono mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="due_date" className="font-mono">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                    className="font-mono mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="font-mono">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="font-mono mt-1"
                    placeholder="e.g., Monthly subscription - December 2024"
                  />
                </div>

                <Button type="submit" className="w-full font-mono">
                  Upload Invoice
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {invoices.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Invoices</h3>
            <p className="text-muted-foreground">Create your first invoice to get started</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Xero Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.map((invoice) => {
                    const company = companies.find(c => c.company_id === invoice.company_id);
                    return (
                      <tr key={invoice.invoice_id} className="hover:bg-muted/5">
                        <td className="px-6 py-4">
                          <span className="font-mono font-bold">{invoice.invoice_number}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono">{company?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-mono font-bold">£{invoice.amount.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-mono border ${getStatusColor(invoice.status)}`}>
                            {invoice.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-muted-foreground">
                            {new Date(invoice.due_date).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {invoice.xero_link ? (
                            <a
                              href={invoice.xero_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-secondary hover:text-secondary/80 text-sm font-mono flex items-center gap-1"
                            >
                              View in Xero
                              <Download className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">No link</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminInvoices;
